import {
  API_VERSION_DEFAULTS,
  BATCH_VEHICLES_API_BASE_CONFIG,
  DEFAULT_API_CONFIG,
} from '~/common/apis/api.constants';
import { DEFAULT_PAGINATION_ROW_PER_PAGE } from '~/common/constants/common.constant';
import type {
  AddVehiclesApiRequest,
  AddVehiclesApiResponse,
  GetIncompleteVehiclesListParams,
  GetPendingConnectionVehiclesListParams,
  GetPendingVerificationVehiclesListParams,
  IncompleteVehicleDetailsApiResponse,
  OffboardVehiclesApiRequest,
  OffboardVehiclesApiResponse,
  PendingConnectionVehicleDetailsApiResponse,
  PendingVerificationVehicleDetailsApiResponse,
} from '~/common/models/addVehicles.model';
import type { ApiResponse } from '~/common/models/apis/apiResponse.model';
import type { PagedResultWithErrors } from '~/common/models/common.model';
import { BDError } from '~/common/models/error.model';
import {
  addCSContinuationTokenHeader,
  addCSFleetIdHeader,
  addCSOrganizationIdHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makeGetPayloadCreator,
  makePostPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';

import { SELECT_ALL_MAX_COUNT } from './addVehiclesSlice.constants';

type PagedVehiclesResult<T> = PagedResultWithErrors<T> & {
  total_items: number;
  continuation_token: string;
};
type PendingVerificationVehiclesResult =
  PagedVehiclesResult<PendingVerificationVehicleDetailsApiResponse>;
type PendingConnectionVehiclesResult =
  PagedVehiclesResult<PendingConnectionVehicleDetailsApiResponse>;
type IncompleteVehiclesResult =
  PagedVehiclesResult<IncompleteVehicleDetailsApiResponse>;

export enum AddVehiclesActionType {
  ADD_VEHICLES = 'addVehicles/addVehicles',
  GET_PENDING_VERIFICATION = 'addVehicles/getPendingVerificationVehicles',
  GET_PENDING_VERIFICATION_EXPORT = 'addVehicles/getPendingVerificationVehiclesExport',
  GET_PENDING_CONNECTION = 'addVehicles/getPendingConnectionVehicles',
  GET_PENDING_CONNECTION_EXPORT = 'addVehicles/getPendingConnectionVehiclesExport',
  GET_INCOMPLETE = 'addVehicles/getIncompleteVehicles',
  GET_INCOMPLETE_EXPORT = 'addVehicles/getIncompleteVehiclesExport',
  OFFBOARD_VEHICLES = 'addVehicles/offboardVehicles',
}

const addVehicles = makeThunk(
  AddVehiclesActionType.ADD_VEHICLES,
  makePostPayloadCreator<
    ApiResponse<AddVehiclesApiResponse>,
    AddVehiclesApiRequest
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.batchOnboardAPI}/boarding/vehicles/onboard`,
    axiosOptions: ({ organizationsId, fleetId }) =>
      addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          BATCH_VEHICLES_API_BASE_CONFIG,
          organizationsId
        ),
        fleetId
      ),
    argAdapter: (params) => ({
      requestBody: { file: params.vinData },
      requestParams: {
        uploadType: params?.uploadType,
      },
    }),
    responseAdapter: (response) => {
      if (response && hasApiResult<AddVehiclesApiResponse>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected Add Vehicles response', {
        data: response,
      });
    },
  })
);

const getPendingVerificationVehicles = makeThunk(
  AddVehiclesActionType.GET_PENDING_VERIFICATION,
  makeGetPayloadCreator<
    ApiResponse<
      PagedResultWithErrors<PendingVerificationVehicleDetailsApiResponse> & {
        total_items: number;
        continuation_token: string;
      }
    >,
    GetPendingVerificationVehiclesListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.pendingVerificationVehiclesAPI}/boarding/vehicles/pending`,
    axiosOptions: ({ organizationsId, fleetsId, continuationToken }) => {
      return addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          addCSContinuationTokenHeader(DEFAULT_API_CONFIG, continuationToken),
          organizationsId
        ),
        fleetsId
      );
    },
    argAdapter: (args) => {
      const filterType = args?.status;
      const size = args.rowsPerPage
        ? String(args.rowsPerPage)
        : String(DEFAULT_PAGINATION_ROW_PER_PAGE);
      return {
        requestParams: {
          size: size,
          vin: args?.vin,
          status: filterType, //right now the API only accepts one filter value
          fleetId: args?.fleetsId,
        },
      };
    },
    responseAdapter: (
      response,
      _,
      args: GetPendingVerificationVehiclesListParams
    ) => {
      if (hasApiResult<PendingVerificationVehiclesResult>(response)) {
        return { ...args, ...response };
      }
      throw new BDError(
        'Unexpected GET Pending Verification Vehicles response: ',
        {
          data: { ...args, response },
        }
      );
    },
  })
);

const getPendingConnectionVehicles = makeThunk(
  AddVehiclesActionType.GET_PENDING_CONNECTION,
  makeGetPayloadCreator<
    ApiResponse<
      PagedResultWithErrors<PendingConnectionVehicleDetailsApiResponse> & {
        total_items: number;
        continuation_token: string;
      }
    >,
    GetPendingConnectionVehiclesListParams
  >({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.connectivityStatusAPI}/views/pendingConnectionVehicles`,
    axiosOptions: ({ organizationsId, fleetsId, continuationToken }) => {
      return addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          addCSContinuationTokenHeader(DEFAULT_API_CONFIG, continuationToken),
          organizationsId
        ),
        fleetsId
      );
    },
    argAdapter: (args) => {
      const size = args.rowsPerPage
        ? String(args.rowsPerPage)
        : String(DEFAULT_PAGINATION_ROW_PER_PAGE);
      return {
        requestParams: {
          size: size,
          vin: args?.vin,
          fleetId: args?.fleetsId,
        },
      };
    },
    responseAdapter: (
      response,
      _,
      args: GetPendingConnectionVehiclesListParams
    ) => {
      if (hasApiResult<PendingConnectionVehiclesResult>(response)) {
        return { ...args, ...response };
      }
      throw new BDError(
        'Unexpected GET Pending Connection Vehicles response: ',
        {
          data: { ...args, response },
        }
      );
    },
  })
);

const getPendingVerificationVehiclesExport = makeThunk(
  AddVehiclesActionType.GET_PENDING_VERIFICATION_EXPORT,
  makeGetPayloadCreator<
    ApiResponse<
      PagedResultWithErrors<PendingVerificationVehicleDetailsApiResponse> & {
        total_items: number;
        continuation_token: string;
      }
    >,
    GetPendingVerificationVehiclesListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.pendingVerificationVehiclesAPI}/boarding/vehicles/pending`,
    axiosOptions: ({ organizationsId, fleetsId, continuationToken }) => {
      return addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          addCSContinuationTokenHeader(DEFAULT_API_CONFIG, continuationToken),
          organizationsId
        ),
        fleetsId
      );
    },
    argAdapter: (args) => {
      const filterType = args?.status;
      return {
        requestParams: {
          size: String(SELECT_ALL_MAX_COUNT),
          vin: args?.vin,
          status: filterType, //right now the API only accepts one filter value
        },
      };
    },
    responseAdapter: (
      response,
      _,
      args: GetPendingVerificationVehiclesListParams
    ) => {
      if (hasApiResult<PendingVerificationVehiclesResult>(response)) {
        return { ...args, ...response };
      }
      throw new BDError('Unexpected GET Pending Vehicles Export response: ', {
        data: { ...args, response },
      });
    },
  })
);

const getPendingConnectionVehiclesExport = makeThunk(
  AddVehiclesActionType.GET_PENDING_CONNECTION_EXPORT,
  makeGetPayloadCreator<
    ApiResponse<
      PagedResultWithErrors<PendingConnectionVehicleDetailsApiResponse> & {
        total_items: number;
        continuation_token: string;
      }
    >,
    GetPendingConnectionVehiclesListParams
  >({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.connectivityStatusAPI}/views/pendingConnectionVehicles`,
    axiosOptions: ({ organizationsId, fleetsId, continuationToken }) => {
      return addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          addCSContinuationTokenHeader(DEFAULT_API_CONFIG, continuationToken),
          organizationsId
        ),
        fleetsId
      );
    },
    argAdapter: (args) => {
      return {
        requestParams: {
          size: String(SELECT_ALL_MAX_COUNT),
          vin: args?.vin,
        },
      };
    },
    responseAdapter: (
      response,
      _,
      args: GetPendingConnectionVehiclesListParams
    ) => {
      if (hasApiResult<PendingConnectionVehiclesResult>(response)) {
        return { ...args, ...response };
      }
      throw new BDError(
        'Unexpected GET Pending Connection Vehicles Export response: ',
        {
          data: { ...args, response },
        }
      );
    },
  })
);

const getIncompleteVehicles = makeThunk(
  AddVehiclesActionType.GET_INCOMPLETE,
  makeGetPayloadCreator<
    ApiResponse<
      PagedResultWithErrors<IncompleteVehicleDetailsApiResponse> & {
        total_items: number;
        continuation_token: string;
      }
    >,
    GetIncompleteVehiclesListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.incompleteVehiclesAPI}/boarding/vehicles/incomplete`,
    axiosOptions: ({ organizationsId, fleetsId, continuationToken }) => {
      return addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          addCSContinuationTokenHeader(DEFAULT_API_CONFIG, continuationToken),
          organizationsId
        ),
        fleetsId
      );
    },
    argAdapter: (args) => {
      const filterType = args?.reason;
      const size = args.rowsPerPage
        ? String(args.rowsPerPage)
        : String(DEFAULT_PAGINATION_ROW_PER_PAGE);
      return {
        requestParams: {
          size: size,
          vin: args?.vin,
          reason: filterType, //right now the API only accepts one filter value
          fleetId: args?.fleetsId,
        },
      };
    },
    responseAdapter: (response, _, args: GetIncompleteVehiclesListParams) => {
      if (hasApiResult<IncompleteVehiclesResult>(response)) {
        return { ...args, ...response };
      }
      throw new BDError('Unexpected GET Incomplete Vehicles response: ', {
        data: { ...args, response },
      });
    },
  })
);

const getIncompleteVehiclesExport = makeThunk(
  AddVehiclesActionType.GET_INCOMPLETE_EXPORT,
  makeGetPayloadCreator<
    ApiResponse<
      PagedResultWithErrors<IncompleteVehicleDetailsApiResponse> & {
        total_items: number;
        continuation_token: string;
      }
    >,
    GetIncompleteVehiclesListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.incompleteVehiclesAPI}/boarding/vehicles/incomplete`,
    axiosOptions: ({ organizationsId, fleetsId, continuationToken }) => {
      return addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          addCSContinuationTokenHeader(DEFAULT_API_CONFIG, continuationToken),
          organizationsId
        ),
        fleetsId
      );
    },
    argAdapter: (args) => {
      const filterType = args?.reason;
      return {
        requestParams: {
          size: String(SELECT_ALL_MAX_COUNT),
          vin: args?.vin,
          status: filterType,
        },
      };
    },
    responseAdapter: (response, _, args: GetIncompleteVehiclesListParams) => {
      if (hasApiResult<IncompleteVehiclesResult>(response)) {
        return { ...args, ...response };
      }
      throw new BDError(
        'Unexpected GET Incomplete Vehicles Export response: ',
        {
          data: { ...args, response },
        }
      );
    },
  })
);

//Much is identical to addVehicles, but the reason we do not use RTK Query codegen OpenAPI
// is because we need to remove the default content-type header (application/json) that is added by codegen.
// This is a workaround until RTK Query codegen supports content type: multipart/form-data headers.
const offboardVehicles = makeThunk(
  AddVehiclesActionType.OFFBOARD_VEHICLES,
  makePostPayloadCreator<
    ApiResponse<OffboardVehiclesApiResponse>,
    OffboardVehiclesApiRequest
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.batchVehicleOffboardAPI}/boarding/vehicles/offboard`,
    axiosOptions: ({ organizationsId, fleetId }) =>
      addCSFleetIdHeader(
        addCSOrganizationIdHeader(
          BATCH_VEHICLES_API_BASE_CONFIG,
          organizationsId
        ),
        fleetId
      ),
    argAdapter: (params) => ({
      requestBody: { file: params.vinData },
    }),
    responseAdapter: (response) => {
      if (response && hasApiResult<OffboardVehiclesApiResponse>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected Offboard Vehicles response', {
        data: response,
      });
    },
  })
);

export const ADD_VEHICLES_ACTIONS = {
  addVehicles,
  getPendingVerificationVehicles,
  getPendingVerificationVehiclesExport,
  getPendingConnectionVehicles,
  getPendingConnectionVehiclesExport,
  getIncompleteVehicles,
  getIncompleteVehiclesExport,
  offboardVehicles,
};
